import { STATE_KEYS } from './StateConstants.js';


/**
 * Progression state management module.
 * Handles memories, dialogue, resurrection, and scientist progression.
 * Receives parent GameStateManager reference for state access and notifications.
 */
export class ProgressionState {
  constructor(parent) {
    this.parent = parent;
  }

  get state() {
    return this.parent.state;
  }

  _notify(key) {
    this.parent._notify(key);
  }

  // ==========================================
  // MEMORIES
  // ==========================================

  getMemories() {
    return this.state.memories;
  }

  hasMemory(memoryId) {
    return this.state.memories.includes(memoryId);
  }

  addMemory(memoryId) {
    if (!this.state.memories.includes(memoryId)) {
      this.state.memories.push(memoryId);
      this._notify(STATE_KEYS.MEMORIES);
      return true;
    }
    return false;
  }

  // ==========================================
  // ENCYCLOPEDIA
  // ==========================================

  getEncyclopedia() {
    return this.state.encyclopedia || [];
  }

  knowsEnemy(enemyId) {
    return (this.state.encyclopedia || []).includes(enemyId);
  }

  learnEnemy(enemyId) {
    if (!this.state.encyclopedia) this.state.encyclopedia = [];
    if (!this.state.encyclopedia.includes(enemyId)) {
      this.state.encyclopedia.push(enemyId);
      this._notify(STATE_KEYS.ENCYCLOPEDIA);
      return true;
    }
    return false;
  }

  // ==========================================
  // DIALOGUE
  // ==========================================

  getCompletedDialogues() {
    return this.state.dialogue?.completedScenes || [];
  }

  isDialogueComplete(sceneId) {
    return this.getCompletedDialogues().includes(sceneId);
  }

  markDialogueComplete(sceneId) {
    if (!this.state.dialogue) {
      this.state.dialogue = { completedScenes: [], choiceHistory: {} };
    }
    if (!this.state.dialogue.completedScenes.includes(sceneId)) {
      this.state.dialogue.completedScenes.push(sceneId);
      return true;
    }
    return false;
  }

  getChoiceHistory() {
    return this.state.dialogue?.choiceHistory || {};
  }

  recordChoice(nodeId, choiceIndex) {
    if (!this.state.dialogue) {
      this.state.dialogue = { completedScenes: [], choiceHistory: {} };
    }
    this.state.dialogue.choiceHistory[`${nodeId}_${choiceIndex}`] = true;
  }

  wasChoiceSelected(nodeId, choiceIndex) {
    return !!this.state.dialogue?.choiceHistory?.[`${nodeId}_${choiceIndex}`];
  }

  // ==========================================
  // RESURRECTION (Medic)
  // ==========================================

  getMedicPosition() {
    return this.state.resurrection?.medicPosition || null;
  }

  setMedicPosition(q, r) {
    if (!this.state.resurrection) {
      this.state.resurrection = { medicPosition: null, depositedSpirit: 0 };
    }
    this.state.resurrection.medicPosition = { q, r };
    this._notify(STATE_KEYS.RESURRECTION);
    this.parent.save();
  }

  getResurrectionSpirit() {
    return this.state.resurrection?.depositedSpirit || 0;
  }

  setResurrectionSpirit(amount) {
    if (!this.state.resurrection) {
      this.state.resurrection = { medicPosition: null, depositedSpirit: 0 };
    }
    this.state.resurrection.depositedSpirit = Math.max(0, Math.min(5, amount));
    this._notify(STATE_KEYS.RESURRECTION);
    this.parent.save();
  }

  hasResurrection() {
    return this.getMedicPosition() !== null && this.getResurrectionSpirit() > 0;
  }

  consumeResurrection() {
    if (!this.state.resurrection) return;
    this.state.resurrection.depositedSpirit = 0;
    this._notify(STATE_KEYS.RESURRECTION);
    this.parent.save();
  }

  // ==========================================
  // SCIENTIST
  // ==========================================

  getScientistDeposited() {
    return this.state.scientist?.totalDeposited || 0;
  }

  addScientistDeposit(amount) {
    if (!this.state.scientist) {
      this.state.scientist = { totalDeposited: 0 };
    }
    this.state.scientist.totalDeposited += amount;
    this.parent.save();
    return this.state.scientist.totalDeposited;
  }

  hasWonGame() {
    return this.hasMemory('world_hacker');
  }

  // ==========================================
  // TRAITS & RELATIONS
  // ==========================================

  getTrait(traitName) {
    return this.state.traits?.[traitName] || 0;
  }

  getTraits() {
    return this.state.traits || {};
  }

  modifyTrait(traitName, delta) {
    if (!this.state.traits) this.state.traits = {};
    const current = this.state.traits[traitName] || 0;
    this.state.traits[traitName] = Math.max(0, Math.min(5, current + delta));
    this._notify(STATE_KEYS.TRAITS);
    this.parent.save();
    return this.state.traits[traitName];
  }

  getRelation(npcName) {
    return this.state.relations?.[npcName] || 0;
  }

  getRelations() {
    return this.state.relations || {};
  }

  modifyRelation(npcName, delta) {
    if (!this.state.relations) this.state.relations = {};
    const current = this.state.relations[npcName] || 0;
    this.state.relations[npcName] = Math.max(0, Math.min(5, current + delta));
    this._notify(STATE_KEYS.RELATIONS);
    this.parent.save();
    return this.state.relations[npcName];
  }

  // ==========================================
  // TRAIT CHECKS (White/Red persistence)
  // ==========================================

  recordFailedWhiteCheck(checkId, trait, traitLevel) {
    if (!this.state.failedWhiteChecks) this.state.failedWhiteChecks = {};
    this.state.failedWhiteChecks[checkId] = { trait, traitAtAttempt: traitLevel };
    this.parent.save();
  }

  canRetryWhiteCheck(checkId, trait) {
    if (!this.state.failedWhiteChecks) return true;
    const record = this.state.failedWhiteChecks[checkId];
    if (!record) return true;
    const currentTrait = this.getTrait(record.trait);
    return currentTrait > record.traitAtAttempt;
  }

  getFailedWhiteChecks() {
    return this.state.failedWhiteChecks || {};
  }

  recordRedCheckResult(checkId, result) {
    if (!this.state.redCheckResults) this.state.redCheckResults = {};
    this.state.redCheckResults[checkId] = result;
    this.parent.save();
  }

  getRedCheckResult(checkId) {
    if (!this.state.redCheckResults) return null;
    return this.state.redCheckResults[checkId] || null;
  }

  getRedCheckResults() {
    return this.state.redCheckResults || {};
  }

  clearCheckHistory() {
    this.state.failedWhiteChecks = {};
    this.state.redCheckResults = {};
    this.parent.save();
  }

  // ==========================================
  // DIALOGUE REWARD CLAIMS
  // ==========================================

  getRewardClaims() {
    return this.state.rewardClaims || {};
  }

  hasRewardClaim(claimKey) {
    if (!this.state.rewardClaims) {
      this.state.rewardClaims = {};
    }
    return this.state.rewardClaims[claimKey] === true;
  }

  recordRewardClaim(claimKey) {
    if (!this.state.rewardClaims) {
      this.state.rewardClaims = {};
    }
    if (this.state.rewardClaims[claimKey] === true) {
      return false;
    }
    this.state.rewardClaims[claimKey] = true;
    this.parent.save();
    return true;
  }

  clearRewardClaims() {
    this.state.rewardClaims = {};
    this.parent.save();
  }
}
